import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { canPrefetch, debounce, isMobileBreakpoint, requestIdleCallback, yieldToMainThread } from '@theme/utilities';

/**
 * Interactive AD Room Banner: a native Horizon slideshow of room images with
 * product hotspots, per-slide bundle pricing sync, and shared product detail
 * modals (na-room-banner-modal-component).
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [bannerSlideshow]
 *
 * @extends {Component<Refs>}
 */
export class AdTheRoomBannerComponent extends Component {
    /** @type {AbortController} */
    #controller = new AbortController();

    /** @type {ReturnType<typeof setTimeout> | undefined} */
    #activateTimeout;

    #handleResize = debounce(() => this.#positionProductCards(), 300);

    connectedCallback() {
        super.connectedCallback();

        const { signal } = this.#controller;

        document.addEventListener('click', this.#handleOutsideClick, { signal });
        window.addEventListener('resize', this.#handleResize, { signal });
        this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideChange, { signal });

        this.#setupHotspots();
        this.#positionProductCards();
        this.#autoActivateDefaultProducts();
        this.#syncBundleFromSlide(this.#activeSlide());
        this.#prefetchActiveSlideModals();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#controller.abort();
        this.#handleResize.cancel();
        clearTimeout(this.#activateTimeout);
    }

    updatedCallback() {
        super.updatedCallback();
        this.#prefetchActiveSlideModals();
    }

    /* ------------------------------------------------------------------ */
    /* Slideshow                                                           */
    /* ------------------------------------------------------------------ */

    /** @returns {any} */
    get #slideshow() {
        return this.querySelector('slideshow-component');
    }

    next() {
        this.#slideshow?.next?.();
    }

    previous() {
        this.#slideshow?.previous?.();
    }

    /** @returns {Element | null} */
    #activeSlide() {
        return this.querySelector('slideshow-slide[aria-hidden="false"]') || this.querySelector('slideshow-slide');
    }

    /** @param {Event} event */
    #handleSlideChange = (event) => {
        const detail = /** @type {any} */ (event).detail;
        const slide = detail?.slide instanceof Element ? detail.slide : this.#activeSlide();
        this.#syncBundleFromSlide(slide);
        requestAnimationFrame(() => this.#positionProductCards());
        this.#prefetchActiveSlideModals();

        if (detail?.previousIndex === detail?.index) return;

        clearTimeout(this.#activateTimeout);
        this.#activateTimeout = setTimeout(() => this.#autoActivateDefaultProducts(), 50);
    };

    /** @param {Element | null} slide */
    #syncBundleFromSlide(slide) {
        if (!(slide instanceof Element)) return;

        const bundleLink = slide.querySelector('.shop_bundle_link');
        const mainButton = this.querySelector('.main_shop_bundle_btn');

        if (mainButton instanceof HTMLAnchorElement && bundleLink instanceof HTMLAnchorElement) {
            mainButton.textContent = bundleLink.textContent;
            mainButton.href = bundleLink.href;
        }

        const pricingArea = this.querySelector('.custom-room-banner__pricing');
        if (pricingArea instanceof HTMLElement) {
            this.#syncText(
                pricingArea.querySelector('.banner-price-compare'),
                slide.querySelector('.banner-compare-price')
            );
            this.#syncText(pricingArea.querySelector('.banner-price-sale'), slide.querySelector('.banner-sale-price'));
            this.#syncText(
                pricingArea.querySelector('.banner-discount-badge'),
                slide.querySelector('.banner-badge-text')
            );
        }
    }

    /**
     * @param {Element | null} target
     * @param {Element | null} source
     */
    #syncText(target, source) {
        if (!(target instanceof HTMLElement)) return;
        const text = source?.textContent?.trim() || '';
        target.textContent = text;
        target.style.display = text ? '' : 'none';
    }

    /* ------------------------------------------------------------------ */
    /* Product hotspots                                                    */
    /* ------------------------------------------------------------------ */

    /** @param {Event} event */
    toggleHotspot(event) {
        if (event instanceof KeyboardEvent) {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
        }

        const trigger = event.target instanceof Element ? event.target.closest('.block-prd-info') : null;
        if (!trigger) return;

        event.stopPropagation();

        if (trigger.classList.contains('active')) return;

        this.#deactivateAllProducts();
        this.#activateProduct(trigger, trigger.querySelector('.product-content-details'));
    }

    #canHover() {
        return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    }

    #setupHotspots() {
        const { signal } = this.#controller;
        const canHover = this.#canHover();

        for (const hotspot of this.querySelectorAll('.number-content')) {
            const productInfo = hotspot.closest('.block-prd-info');
            const details = productInfo?.querySelector('.product-content-details');
            if (!productInfo || !details) continue;

            hotspot.addEventListener('mousedown', (event) => event.stopPropagation(), { signal });

            if (!canHover) continue;

            hotspot.addEventListener(
                'mouseenter',
                () => {
                    this.#deactivateAllProducts();
                    this.#activateProduct(productInfo, details);
                },
                { signal }
            );
        }
    }

    /** @param {Event} event */
    #handleOutsideClick = (event) => {
        const target = event.target;
        if (!(target instanceof Node)) return;
        if (target instanceof Element && target.closest('.block-prd-info')) return;

        for (const product of this.querySelectorAll('.block-prd-info.active')) {
            const details = product.querySelector('.product-content-details');
            this.#deactivateProduct(product, details);
        }
    };

    /**
     * @param {Element} productInfo
     * @param {Element | null} details
     */
    #activateProduct(productInfo, details) {
        productInfo.classList.add('active');
        details?.classList.add('active');
        yieldToMainThread().then(() => {
            if (this.isConnected) this.#prepareModal(productInfo);
        });
    }

    /**
     * Hotspot hover is the earliest signal the user may open this modal, so this is
     * where the deferred slides are hydrated and their images warmed up.
     * @param {Element} productInfo
     */
    #prepareModal(productInfo) {
        const opener = productInfo.querySelector('[data-modal-id]');
        const modalId = opener instanceof HTMLElement ? opener.dataset.modalId : null;
        if (!modalId) return;
        const modal = /** @type {any} */ (this.querySelector(`#${CSS.escape(modalId)}`));
        if (!modal) return;
        if (typeof modal.prefetch === 'function') modal.prefetch();
        if (typeof modal.hydrate === 'function') modal.hydrate();
        for (const img of modal.querySelectorAll('img.room-info__modal-image')) {
            if (!(img instanceof HTMLImageElement) || img.complete) continue;
            const src = img.currentSrc || img.src;
            if (src) new Image().src = src;
        }
    }

    /**
     * @param {Element} productInfo
     * @param {Element | null} details
     */
    #deactivateProduct(productInfo, details) {
        productInfo.classList.remove('active');
        details?.classList.remove('active');
    }

    #deactivateAllProducts() {
        for (const el of this.querySelectorAll('.block-prd-info.active')) {
            this.#deactivateProduct(el, el.querySelector('.product-content-details'));
        }
    }

    #autoActivateDefaultProducts() {
        const activeSlide = this.#activeSlide() || this;
        const defaultOpen = activeSlide.querySelector('.block-prd-info[data-open-by-default="true"]');
        const fallback = defaultOpen || activeSlide.querySelector('.block-prd-info.free_product');

        if (fallback?.classList.contains('active')) return;

        this.#deactivateAllProducts();
        if (fallback) this.#activateProduct(fallback, fallback.querySelector('.product-content-details'));
    }

    #prefetchActiveSlideModals() {
        if (!canPrefetch()) return;

        requestIdleCallback(() => {
            const slide = this.#activeSlide() || this;
            for (const opener of slide.querySelectorAll('[data-modal-id]')) {
                if (!(opener instanceof HTMLElement) || !opener.dataset.modalId) continue;
                const modal = /** @type {any} */ (this.querySelector(`#${CSS.escape(opener.dataset.modalId)}`));
                modal?.prefetch?.();
            }
        });
    }

    /* ------------------------------------------------------------------ */
    /* Product modal (delegates to na-room-banner-modal-component)           */
    /* ------------------------------------------------------------------ */

    /** @param {Event} event */
    openModal(event) {
        const opener = event.target instanceof Element ? event.target.closest('[data-modal-id]') : null;
        const id = opener instanceof HTMLElement ? opener.dataset.modalId : null;
        if (!id) return;

        const modal = /** @type {any} */ (this.querySelector(`#${CSS.escape(id)}`));
        if (modal && typeof modal.openModal === 'function') modal.openModal();
    }

    /* ------------------------------------------------------------------ */
    /* Tooltip positioning                                                 */
    /* ------------------------------------------------------------------ */

    #positionProductCards() {
        /** @type {{ card: HTMLElement, details: HTMLElement, marker: Element }[]} */
        const cards = [];

        for (const card of this.querySelectorAll('.block-prd-info')) {
            if (!(card instanceof HTMLElement)) continue;

            const details = card.querySelector('.glider-detail');
            const marker = card.querySelector('.number-content');
            if (!(details instanceof HTMLElement) || !marker) continue;

            cards.push({ card, details, marker });
        }

        for (const { details } of cards) {
            Object.assign(details.style, {
                position: 'absolute',
                left: '50%',
                top: '0',
                transform: 'translateX(-50%)',
            });
        }

        const isMobile = isMobileBreakpoint();
        const spacing = 10;
        const gapAboveMobile = 16;

        const measurements = cards.map(({ card, details, marker }) => {
            const markerAnchor = marker.querySelector('.number') || marker;
            const cardRect = card.getBoundingClientRect();
            const markerRect = markerAnchor.getBoundingClientRect();
            const detailsRect = details.getBoundingClientRect();
            const container = card.offsetParent || document.body;
            const containerRect = container.getBoundingClientRect();
            const space = {
                above: markerRect.top - containerRect.top,
                below: containerRect.bottom - markerRect.bottom,
                left: markerRect.left - containerRect.left,
                right: containerRect.right - markerRect.right,
            };

            let placeAbove = false;
            if (isMobile) {
                if (space.above >= detailsRect.height + gapAboveMobile) {
                    placeAbove = true;
                } else if (space.below >= detailsRect.height + spacing) {
                    placeAbove = false;
                } else {
                    placeAbove = space.above >= space.below;
                }
            } else if (space.below >= detailsRect.height + spacing) {
                placeAbove = false;
            } else if (space.above >= detailsRect.height + spacing) {
                placeAbove = true;
            }

            let left = '50%';
            let transformX = '-50%';

            if (space.right < detailsRect.width / 2) {
                left = '100%';
                transformX = '-100%';
            } else if (space.left < detailsRect.width / 2) {
                left = '0';
                transformX = '0';
            }

            return { card, details, cardRect, markerRect, placeAbove, left, transformX };
        });

        for (const { details, left, transformX } of measurements) {
            Object.assign(details.style, {
                left,
                transform: `translateX(${transformX})`,
                top: '0',
            });
        }

        const tops = measurements.map(({ details, cardRect, markerRect, placeAbove }) => {
            const detailsRectFinal = details.getBoundingClientRect();
            let top;
            if (isMobile && placeAbove) {
                top = markerRect.top - cardRect.top - detailsRectFinal.height - gapAboveMobile;
            } else if (isMobile) {
                top = markerRect.bottom - cardRect.top + spacing;
            } else if (placeAbove) {
                top = markerRect.top - cardRect.top - detailsRectFinal.height - spacing;
            } else {
                top = markerRect.bottom - cardRect.top + spacing;
            }
            return { details, top };
        });

        for (const { details, top } of tops) {
            details.style.top = `${top}px`;
        }
    }
}

if (!customElements.get('na-the-room-banner-component')) {
    customElements.define('na-the-room-banner-component', AdTheRoomBannerComponent);
}
